// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    CircularProgress,
    Button
} from '@mui/material';

import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';

import { axiosInstance } from 'utils/axios';
import useFetchData from 'hooks/useFetchData';

import UpdateCustomerModal from './UpdateCustomerModal';
import BankChangeLogsModal from './BankChangeLogsModal';
import TransactionModal from './TransactionModal';
import CustomerTasklistModal from './CustomerTasklistModal';
import ResetTasklistModal from './ResetTasklistModal';
import EditCustomerModal from './EditCustomerModal';
import UpdateCustomerPermissionModal from './UpdateCustomerPermissionModal';
import TransactionHistoryModal from './TransactionHistoryModal';
import { successAlert, errorAlert, warningAlert } from 'ui-component/Toasts';
import { deepPurple, grey } from '@mui/material/colors';

import { useTranslation } from "react-i18next";
import { padding } from '@mui/system';
import CustomerNotificationsModal from './CustomerNotificationsModal';

const columns = [
    { id: 'id', label: 'ID', minWidth: 50 },
    { id: 'details', label: '详情', minWidth: 170 },
    { id: 'condition', label: '账户管理', minWidth: 170 },
    { id: 'bankDetails', label: '银行/联系信息', minWidth: 170 },
    { id: 'ip', label: 'IP', minWidth: 170 },
    { id: 'missionDetails', label: '任务计划', minWidth: 170 },
    { id: 'setting', label: '设置', minWidth: 170 },
    { id: 'action', label: '状态', minWidth: 170 },
];

function CustomerTable({
    fetchCustomerLoading,
    customers,
    customerTasklistStatusList,
    customerStatus,
    fetchCustomers,
    loginUsernameOptions
}) {
    const { t } = useTranslation();

    const token = useSelector(selectCurrentToken);

    const tableRef = useRef(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const [tasklist, setTasklist] = useState([]);
    const [transactionModalOpen, setTransactionModalOpen] = useState(false);
    const [customerTasklistModalOpen, setCustomerTasklistModalOpen] = useState(false);
    const [resetTasklistModalOpen, setResetTasklistModalOpen] = useState(false);
    const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
    const [updateCustomerPermissionModalOpen, setUpdateCustomerPermissionModalOpen] = useState(false);
    const [transactionHistoryModalOpen, setTransactionHistoryModalOpen] = useState(false);
    const [customerNotificationModalOpen, setCustomerNotificationModalOpen] = useState(false);

    const [resetModalActiveTab, setResetModalActiveTab] = useState('resetTasklist');
    const [editCustomerModalActiveTab, setEditCustomerModalActiveTab] = useState('personalDetails');
    const [transactionModalActiveTab, setTransactionModalActiveTab] = useState('deposit');
    const [customerTasklistModalActiveTab, setCustomerTasklistModalActiveTab] = useState('allTasks');
    const [customerNotificationModalActiveTab, setCustomerNotificationModalActiveTab] = useState('insertNotification');
    
    const [selectedCustomerRow, setSelectedCustomerRow] = useState(null);
    const [isActualAccount, setIsActualAccount] = useState(null);
    const [isAllowToTakeTask, setIsAllowToTakeTask] = useState(null);
    const [isAllowToCompleteTask, setIsAllowToCompleteTask] = useState(null);
    const [isAllowToWithdrawWithoutTask, setIsAllowToWithdrawWithoutTask] = useState(null);
    const [isAllowToWithdraw, setIsAllowToWithdraw] = useState(null);
    const [isAllowToWithdrawWhenPresetTask, setIsAllowToWithdrawWhenPresetTask] = useState(null);
    const [isAllowToUseReferralCode, setIsAllowToUseReferralCode] = useState(null);
    const [isUnconditionalWithdrawalAllowed, setIsUnconditionalWithdrawalAllowed] = useState(null);

    const openResetTasklistModal = (row, tab) => {
        setSelectedCustomerRow(row);
        setResetModalActiveTab(tab);
        setResetTasklistModalOpen(true);
    };

    const openEditCustomerModal = (row, tab) => {
        setSelectedCustomerRow(row);
        setEditCustomerModalActiveTab(tab);
        setEditCustomerModalOpen(true);
    };

    const openTransactionModal = (row, tab) => {
        setSelectedCustomerRow(row);
        setTransactionModalActiveTab(tab);
        setTransactionModalOpen(true);
    };

    const openCustomerTasklistModal = (row, tab) => {
        setSelectedCustomerRow(row);
        setCustomerTasklistModalActiveTab(tab);
        setCustomerTasklistModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isActualAccount = (row) => {
        setSelectedCustomerRow(row);
        setIsActualAccount(!row.isActualAccount);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isAllowToTakeTask = (row) => {
        setSelectedCustomerRow(row);
        setIsAllowToTakeTask(!row.isAllowToTakeTask);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_IsAllowToCompleteTask = (row) => {
        setSelectedCustomerRow(row);
        setIsAllowToCompleteTask(!row.isAllowToCompleteTask);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isAllowToWithdrawWithoutTask = (row) => {
        setSelectedCustomerRow(row);
        setIsAllowToWithdrawWithoutTask(!row.isAllowToWithdrawWithoutTask);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isAllowToWithdraw = (row) => {
        setSelectedCustomerRow(row);
        setIsAllowToWithdraw(!row.isAllowToWithdraw);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isAllowToWithdrawWhenPresetTask = (row) => {
        setSelectedCustomerRow(row);
        setIsAllowToWithdrawWhenPresetTask(!row.isAllowToWithdrawWhenPresetTask);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isAllowToUseReferralCode = (row) => {
        setSelectedCustomerRow(row);
        setIsAllowToUseReferralCode(!row.isAllowToUseReferralCode);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openUpdateCustomerPermissionModal_isUnconditionalWithdrawalAllowed = (row) => {
        setSelectedCustomerRow(row);
        setIsUnconditionalWithdrawalAllowed(!row.isUnconditionalWithdrawalAllowed);
        setUpdateCustomerPermissionModalOpen(true);
    };

    const openTransactionHistoryModal = (row) => {
        setSelectedCustomerRow(row);
        setTransactionHistoryModalOpen(true);
    };

    const openCustomerNotificationModal = (row, tab) => {
        setSelectedCustomerRow(row);
        setCustomerNotificationModalActiveTab(tab);
        setCustomerNotificationModalOpen(true);
    };

    const fetchTaskList = async (filterTaskValueRangeStart = null, filterTaskValueRangeEnd = null) => {
        let params = '';
        if (filterTaskValueRangeStart >= 0 && filterTaskValueRangeEnd >= 0) {
            params = `?taskValueRangeStart=${filterTaskValueRangeStart}&taskValueRangeEnd=${filterTaskValueRangeEnd}`;
        }
        let taskListURL = '/Tasklist/All' + params;
        try {
            const res = await axiosInstance.get(taskListURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                return res.data;
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        }
    };
    const [isUserAllowEdit, setIsUserAllowEdit] = useState(false);
    const fetchIsUserAllowEdit = async () => {
        try {
            const res = await axiosInstance.get('IsAllowEdit', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setIsUserAllowEdit(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        Promise.all([])
            .then((values) => {
                setTasklist(values[0]);
                fetchIsUserAllowEdit();
            })
            .catch((error) => {
                console.error('An error occurred:', error);
            });
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const [updateCustomerStatusButtonDisabled, setUpdateCustomerStatusButtonDisabled] = useState(false);
    const handleUpdateCustomerStatus = async (customerID, isDisabled) => {
        setUpdateCustomerStatusButtonDisabled(true);
        try {
            const res = await axiosInstance.put(
                '/CustomerPassword',
                {
                    customerID: customerID,
                    newPassword: '',
                    isDisabled: isDisabled
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            console.log('res.status', res.status);

            if (res.status === 200) {
                if (isDisabled){
                    console.log('成功冻结账号');
                    successAlert('成功冻结账号');
                    // fetchCustomers();
                    handleRefresh();
                } else{
                    console.log('成功激活账号');
                    successAlert('成功激活账号');
                    // fetchCustomers();
                    handleRefresh();
                }
            } else {
                errorAlert('发生错误');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setUpdateCustomerStatusButtonDisabled(false);
        }
    };

    const handleRefresh = async () => {
        console.log('handleRefresh 1234');
        const scrollTop = tableRef.current?.scrollTop;
        const scrollY = window.scrollY;
        await fetchCustomers(); // Calls the function from index.js
        setTimeout(() => {
            if (tableRef.current) {
                tableRef.current.scrollTop = scrollTop;
            }
            window.scrollTo({ top: scrollY, behavior: 'auto' });
        }, 0);
    };

    return (
        <Box sx={{ width: '100%', minHeight: '200px' }}>
            {fetchCustomerLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer ref={tableRef} sx={{ height: '100vh' }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    <TableCell key="id" style={{ minWidth: 50 }}>#</TableCell>
                                    <TableCell key="details" style={{ minWidth: 170 }}>{t("customer.details")}</TableCell>
                                    <TableCell key="condition" style={{ minWidth: 170 }}>{t("customer.accountManagement")}</TableCell>
                                    <TableCell key="bankDetails" style={{ minWidth: 170 }}>{t("customer.bankAccountDetails")}</TableCell>
                                    <TableCell key="ip" style={{ minWidth: 170 }}>IP</TableCell>
                                    <TableCell key="missionDetails" style={{ minWidth: 170 }}>{t("customer.taskPlan")}</TableCell>
                                    <TableCell key="setting" style={{ minWidth: 170 }}>{t("customer.setting")}</TableCell>
                                    <TableCell key="action" style={{ minWidth: 170 }}>{t("customer.status")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {customers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                                            <TableCell key='id'>
                                                {row.id}
                                            </TableCell>
                                            <TableCell key='details' style={{fontSize: "12px"}}>
                                                {t("customer.code")} : {row.numberCode} <br/>
                                                {row.loginUserName} ( {row.name} ) <br/>
                                                {t("customer.actualWalletBalance")} : {row.actualWalletBalance} <br/>
                                                {t("customer.walletBalance")} : {row.assetBalance} <br/>
                                                {t("customer.loginPassword")} : {row.loginPassword} <br/>
                                                {t("customer.withdrawalPassword")} : {row.withdrawalPassword} <br/>
                                                {t("customer.phoneNumber")} : {row.phoneNumber} <br/>
                                                {t("customer.referralCode")} : {row.referralCode} <br/>
                                                {t("customer.by")} : {row.createdBy} {t("customer.created")} <br/>
                                                {t("customer.by")} : {row.referrerCustomerLoginUserName} {t("customer.updated")} <br/>
                                                {t("customer.createdAt")} {
                                                `${row.createdDate.split('T')[0]} ${new Date(
                                                    '1970-01-01T' + row.createdDate.split('T')[1] + 'Z'
                                                ).toLocaleTimeString('en-US', {
                                                    timeZone: 'UTC',
                                                    hour12: true,
                                                    hour: 'numeric',
                                                    minute: 'numeric',
                                                    second: 'numeric'
                                                })}`} <br/>
                                                { row.loginLogCreatedDate && 
                                                <>
                                                    {t("customer.lastOnline")} {' '}
                                                    {row.loginLogCreatedDate.split('T')[0]} {new Date(
                                                        '1970-01-01T' + row.loginLogCreatedDate.split('T')[1] + 'Z'
                                                    ).toLocaleTimeString('en-US', {
                                                        timeZone: 'UTC',
                                                        hour12: true,
                                                        hour: 'numeric',
                                                        minute: 'numeric',
                                                        second: 'numeric'
                                                    })}
                                                </>}
                                            </TableCell>
                                            <TableCell key='condition' style={{fontSize: "12px"}}>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isActualAccount(row)}>
                                                    {row.isActualAccount === true ? <>&#9989;{t("customer.actualAccount")}</> : <>&#10060;{t("customer.testingAccount")}</>} 
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isAllowToTakeTask(row)}>
                                                    {row.isAllowToTakeTask === true ? <>&#9989;{t("customer.allowToTakeTask")}</> : <>&#10060;{t("customer.disallowToTakeTask")}</>} 
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_IsAllowToCompleteTask(row)}>
                                                    {row.isAllowToCompleteTask === true ? <>&#9989;{t("customer.allowToCompleteTask")}</> : <>&#10060;{t("customer.disallowToCompleteTask")}</>}
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isAllowToWithdrawWithoutTask(row)}>
                                                    {row.isAllowToWithdrawWithoutTask === true ? <>&#9989;{t("customer.allowToWithdrawWithoutTask")}</> : <>&#10060;{t("customer.disallowToWithdrawWithoutTask")}</>}
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isAllowToWithdraw(row)}>
                                                    {row.isAllowToWithdraw === true ? <>&#9989;{t("customer.allowToWithdraw")}</> : <>&#10060;{t("customer.disallowToWithdraw")}</>}
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isAllowToWithdrawWhenPresetTask(row)}>
                                                    {row.isAllowToWithdrawWhenPresetTask === true ? <>&#9989;{t("customer.allowToWithdrawWhenPresetTask")}</> : <>&#10060;{t("customer.disallowToWithdrawWhenPresetTask")}</>}
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isAllowToUseReferralCode(row)}>
                                                    {row.isAllowToUseReferralCode === true ? <>&#9989;{t("customer.allowToUseReferralCode")}</> : <>&#10060;{t("customer.disallowToUseReferralCode")}</>} 
                                                </Button><br/>
                                                <Button style={{fontSize: "12px", padding: 'unset'}} onClick={() => openUpdateCustomerPermissionModal_isUnconditionalWithdrawalAllowed(row)}>
                                                    {row.isUnconditionalWithdrawalAllowed === true ? <>&#9989;{t("customer.allowUnconditionalWithdrawal")}</> : <>&#10060;{t("customer.disallowUnconditionalWithdrawal")}</>} 
                                                </Button><br/>
                                            </TableCell>
                                            <TableCell key='bankDetails' style={{fontSize: "12px"}}>
                                                {/* 电话号码 : {row.phoneNumber} <br/>
                                                银行名称 : {row.bankVendorName} <br/>
                                                账户号 : {row.bankAccount} <br/>
                                                持卡人姓名 : {row.bankHolderName} */}

                                                {
                                                    row.bankVendorID === 1 && (
                                                        <>
                                                            {t("customer.bankName")} : {row.manualBankName} <br/>
                                                            {t("customer.bankAccountHolder")} : {row.bankHolderName} <br/>
                                                            {/* 账户号 : {row.bankAccount} <br/> */}
                                                            {t("customer.IBAN")} : {row.secondBankAccountNumber}
                                                        </>
                                                    )
                                                }
                                                {
                                                    row.bankVendorID === 2 && (
                                                        <>
                                                            用户全名 : {row.bankHolderName} <br/>
                                                            Wise Email : {row.bankAccount} <br/>
                                                            Wise IBAN : {row.secondBankAccountNumber}
                                                        </>
                                                    )
                                                }
                                                {
                                                    row.bankVendorID === 3 && (
                                                        <>
                                                            用户全名 : {row.bankHolderName} <br/>
                                                            Revolut Revtag : {row.bankAccount} <br/>
                                                            Revolut IBAN : {row.secondBankAccountNumber}
                                                        </>
                                                    )
                                                }
                                                {
                                                    row.bankVendorID === 4 && (
                                                        <>
                                                            N26 全名 : {row.bankHolderName} <br/>
                                                            N26 IBAN : {row.secondBankAccountNumber}
                                                        </>
                                                    )
                                                }
                                                {
                                                    row.bankVendorID === 5 && (
                                                        <>
                                                            钱包地址 : {row.bankAccount} <br/>
                                                            区块链/网络 : {row.manualBankName}
                                                        </>
                                                    )
                                                }
                                                <br/>
                                                {t("customer.phoneNumber")} : {row.phoneNumber}
                                            </TableCell>
                                            <TableCell key='ip' style={{fontSize: "12px"}}>
                                                {t("customer.ipAddress")} : {row.loginLogIPAddress} <br/>
                                                {t("customer.ipCountry")} : {row.loginLogCountry} <br/>
                                                {t("customer.ipCity")} : {row.loginLogCity} <br/>
                                                {t("customer.ipRegion")} : {row.loginLogRegion} <br/>
                                                {t("customer.ipISP")} : {row.loginLogISP}<br/>
                                                {/* {
                                                    row.isRepeatCustomer &&
                                                    <>
                                                        <span style={{color: 'red'}}>
                                                            {t("customer.duplicateIPRegisterLogin")}
                                                        </span>
                                                    </>
                                                } */}
                                                {
                                                    row.duplicateIPAddress && row.duplicateIPAddress.length > 0 &&
                                                    <>
                                                        <span style={{color: 'red'}}>
                                                            {t("customer.duplicateIPRegisterLogin")}
                                                        </span>
                                                        <br/>
                                                        {row.duplicateIPAddress.map((address, index) => (
                                                            <span key={index} style={{ color: 'red' }}>
                                                                {address}<br />
                                                            </span>
                                                        ))}
                                                    </>
                                                }
                                            </TableCell>
                                            <TableCell key='missionDetails' style={{fontSize: "12px"}}>
                                                {t("customer.level")} : {row.ambassadorLevelName} <br/>
                                                {t("customer.everyDayTask")} : {row.eachSetTaskNumber} <br/> 
                                                {row.totalTaskSet}{t("customer.setTask")}  <br/>
                                                {t("customer.todayCompleted")}: {row.todayCompletedTaskCount ?? '0'} {t("customer.task")} / ({row.todayCompletedTaskSetCount ?? '0'} {t("customer.set")})  <br/>
                                                {t("customer.completed")}: {row.allTimeLastTaskNumber ?? '0'}  <br/>
                                                {t("customer.currentTask")}: {row.currentTaskNumber ?? '0'}  <br/>
                                                {t("customer.totalDeposit")}: $ {row.totalDepositAmount ?? '0'}  <br/>
                                                {t("customer.todayCommission")}: $ {row.todayTaskProfit ?? '0'}  <br/>
                                                {t("customer.totalCommission")}: $ {row.allTimeTaskProfit ?? '0'}  <br/>
                                                {t("customer.currentTaskProfit")}: $ {row.currentPendingTaskProfit ?? '0'} <br/>
                                                {t("customer.creditScore")}: {row.creditScore ?? '0'}
                                            </TableCell>
                                            <TableCell key='setting' style={{fontSize: "12px"}}>
                                                <Button
                                                    onClick={() => openCustomerTasklistModal(row, 'allTasks')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.task")}
                                                </Button>
                                                {' '}
                                                <Button
                                                    onClick={() => openCustomerTasklistModal(row, 'comboTaskSetting')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.comboTask")}
                                                </Button>
                                                <br/><br/>
                                                {/* <CustomerTasklistModal customerTasklistModalOpen={customerTasklistModalOpen} setCustomerTasklistModalOpen={setCustomerTasklistModalOpen}
                                                    activeTab='allTasks' buttonName='任务' customerRow={row} fetchCustomers={fetchCustomers} />
                                                    {' '}
                                                <CustomerTasklistModal customerTasklistModalOpen={customerTasklistModalOpen} setCustomerTasklistModalOpen={setCustomerTasklistModalOpen}
                                                    activeTab='comboTaskSetting' buttonName='连单' customerRow={row} fetchCustomers={fetchCustomers} /> <br/><br/> */}

                                                {/* <TransactionModal transactionModalOpen={transactionModalOpen} setTransactionModalOpen={setTransactionModalOpen}
                                                    activeTab='vipLevel' buttonName='会员等级' customerRow={row} fetchCustomers={fetchCustomers} />
                                                    {' '}
                                                <TransactionModal transactionModalOpen={transactionModalOpen} setTransactionModalOpen={setTransactionModalOpen}
                                                    activeTab='deposit' buttonName='加扣款' customerRow={row} fetchCustomers={fetchCustomers} /> <br/><br/> */}
                                                    
                                                <Button
                                                    onClick={() => openTransactionModal(row, 'vipLevel')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.level")}
                                                </Button>
                                                {' '}
                                                <Button
                                                    onClick={() => openTransactionModal(row, 'deposit')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.editBalance")}
                                                </Button>
                                                <br/><br/>
                                                <Button
                                                    onClick={() => openResetTasklistModal(row, 'resetTasklist')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.resetTask")}
                                                </Button>
                                                {' '}
                                                <Button
                                                    onClick={() => openResetTasklistModal(row, 'resetTasklistRecords')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.resetRecord")}
                                                </Button><br/><br/>
                                                {/* <ResetTasklistModal resetTasklistModalOpen={resetTasklistModalOpen} setResetTasklistModalOpen={setResetTasklistModalOpen}
                                                    activeTab='resetTasklist' buttonName='重置任务' customerRow={row} fetchCustomers={fetchCustomers} />
                                                    {' '}
                                                <ResetTasklistModal resetTasklistModalOpen={resetTasklistModalOpen} setResetTasklistModalOpen={setResetTasklistModalOpen}
                                                    activeTab='resetTasklistRecords' buttonName='重置记录' customerRow={row} fetchCustomers={fetchCustomers} /> <br/><br/> */}
                                                {/* <EditCustomerModal editCustomerModalOpen={editCustomerModalOpen} setEditCustomerModalOpen={setEditCustomerModalOpen}
                                                    activeTab='personalDetails' buttonName='编辑用户' customerRow={row} fetchCustomers={fetchCustomers} /> */}
                                                    
                                                <Button
                                                    onClick={() => openEditCustomerModal(row, 'personalDetails')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.editProfile")}
                                                </Button>
                                                {' '}
                                                <Button
                                                    onClick={() => openCustomerTasklistModal(row, 'comboTaskHistory')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.comboTaskHistory")}
                                                </Button>
                                                {' '}<br/><br/>
                                                <Button
                                                    onClick={() => openTransactionHistoryModal(row)}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.transactionHistory")}
                                                </Button>
                                                {' '}
                                                <Button
                                                    onClick={() => openCustomerNotificationModal(row, 'insertNotification')}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: deepPurple[500],
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        '&:hover': {
                                                            color: deepPurple[500],
                                                            backgroundColor: 'white!'
                                                        },
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("customer.customerNotification")}
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                {
                                                    row.customerStatusID === 1 &&
                                                    <Button
                                                        onClick={() => handleUpdateCustomerStatus(row.id, true)}
                                                        sx={{
                                                            border: 'none',
                                                            backgroundColor: 'red',
                                                            width: 'fit-content',
                                                            height: 'fit-content',
                                                            color: 'white',
                                                            '&.Mui-selected, &.Mui-selected:hover': {
                                                                color: 'red',
                                                                backgroundColor: 'white'
                                                            },
                                                            fontSize: '12px'
                                                        }}
                                                        disabled={updateCustomerStatusButtonDisabled}
                                                    >
                                                        {t("customer.deactivate")}
                                                    </Button>
                                                }
                                                <br/>
                                                {
                                                    row.customerStatusID === 2 &&
                                                    <Button
                                                        onClick={() => handleUpdateCustomerStatus(row.id, false)}
                                                        sx={{
                                                            border: 'none',
                                                            backgroundColor: 'green',
                                                            width: 'fit-content',
                                                            height: 'fit-content',
                                                            color: 'white',
                                                            '&.Mui-selected, &.Mui-selected:hover': {
                                                                color: 'green',
                                                                backgroundColor: 'white'
                                                            },
                                                            fontSize: '12px',
                                                            marginTop: '15px'
                                                        }}
                                                        disabled={updateCustomerStatusButtonDisabled}
                                                    >
                                                        {t("customer.activate")}
                                                    </Button>
                                                }
                                            </TableCell>
                                            {/* {isUserAllowEdit && (
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px'
                                                        }}
                                                    >
                                                        <UpdateCustomerModal
                                                            customer={row}
                                                            customerStatus={customerStatus}
                                                            customerBankVendor={customerBankVendor}
                                                            customerGroups={customerGroups}
                                                            customerInquiries={customerInquiries}
                                                            fetchCustomers={fetchCustomers}
                                                            memberLevels={memberLevels}
                                                            memberTypes={memberTypes}
                                                            loginUsernameOptions={loginUsernameOptions}
                                                        />
                                                    </Box>
                                                </TableCell> 
                                            )} */}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={customers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {selectedCustomerRow && (
                <ResetTasklistModal
                    resetTasklistModalOpen={resetTasklistModalOpen}
                    setResetTasklistModalOpen={setResetTasklistModalOpen}
                    activeTab={resetModalActiveTab}
                    buttonName=""
                    customerRow={selectedCustomerRow}
                    fetchCustomers={fetchCustomers}
                    handleRefresh={handleRefresh}
                />
            )}

            {selectedCustomerRow && (
                <EditCustomerModal
                    editCustomerModalOpen={editCustomerModalOpen}
                    setEditCustomerModalOpen={setEditCustomerModalOpen}
                    activeTab={editCustomerModalActiveTab}
                    buttonName=""
                    customerRow={selectedCustomerRow}
                    fetchCustomers={fetchCustomers}
                    handleRefresh={handleRefresh}
                />
            )}

            {selectedCustomerRow && (
                <TransactionModal
                    transactionModalOpen={transactionModalOpen}
                    setTransactionModalOpen={setTransactionModalOpen}
                    activeTab={transactionModalActiveTab}
                    buttonName=""
                    customerRow={selectedCustomerRow}
                    fetchCustomers={fetchCustomers}
                    handleRefresh={handleRefresh}
                />
            )}

            {selectedCustomerRow && (
                <CustomerTasklistModal
                    customerTasklistModalOpen={customerTasklistModalOpen}
                    setCustomerTasklistModalOpen={setCustomerTasklistModalOpen}
                    activeTab={customerTasklistModalActiveTab}
                    buttonName=""
                    customerRow={selectedCustomerRow}
                    fetchCustomers={fetchCustomers}
                    handleRefresh={handleRefresh}
                />
            )}

            {selectedCustomerRow && (
                <UpdateCustomerPermissionModal
                    updateCustomerPermissionModalOpen={updateCustomerPermissionModalOpen}
                    setUpdateCustomerPermissionModalOpen={setUpdateCustomerPermissionModalOpen}
                    customerRow={selectedCustomerRow}
                    fetchCustomers={fetchCustomers}
                    isActualAccount={isActualAccount}
                    isAllowToTakeTask={isAllowToTakeTask}
                    isAllowToCompleteTask={isAllowToCompleteTask}
                    isAllowToWithdrawWithoutTask={isAllowToWithdrawWithoutTask}
                    isAllowToWithdraw={isAllowToWithdraw}
                    isAllowToWithdrawWhenPresetTask={isAllowToWithdrawWhenPresetTask}
                    isAllowToUseReferralCode={isAllowToUseReferralCode}
                    isUnconditionalWithdrawalAllowed={isUnconditionalWithdrawalAllowed}
                    handleRefresh={handleRefresh}
                />
            )}

            {selectedCustomerRow && (
                <TransactionHistoryModal
                    transactionHistoryModalOpen={transactionHistoryModalOpen}
                    setTransactionHistoryModalOpen={setTransactionHistoryModalOpen}
                    customerRow={selectedCustomerRow}
                    handleRefresh={handleRefresh}
                />
            )}

            {selectedCustomerRow && (
                <CustomerNotificationsModal
                    customerNotificationModalOpen={customerNotificationModalOpen}
                    setCustomerNotificationModalOpen={setCustomerNotificationModalOpen}
                    activeTab={customerNotificationModalActiveTab}
                    buttonName=""
                    customerRow={selectedCustomerRow}
                    handleRefresh={handleRefresh}
                />
            )}
        </Box>
    );
}

export default CustomerTable;
